import { createRng, RandomFn } from './seededRng.ts';
import { FirePosition, getStartingFirePositionsFromRng } from './firePositions.ts';
import { GameConfig, GridType } from '../types.ts';
import { fireSpreadService } from '../services/fireSpreadService.ts';

export type PreBurn = [row: number, col: number];

export interface ContainerGameResult {
  finalGrid: GridType;
  preBurnsApplied: number;
  gameStatus: 'win' | 'lose' | null;
  survivalPercentage: number;
}

/** One RNG stream for fire layout plus all subsequent spread rolls. */
export function createSeededGameRng(
  seed: number,
  gridSize: number,
  startingFires: number
): { rng: RandomFn; firePositions: FirePosition[] } {
  const rng = createRng(seed);
  const firePositions = getStartingFirePositionsFromRng(rng, gridSize, startingFires);
  return { rng, firePositions };
}

/**
 * Headless container (turn-based) game: one pre-burn, one fire spread per step.
 * Matches GameContext when each burn is followed immediately by spread.
 */
export function runContainerGame(
  seed: number,
  config: GameConfig,
  preBurns: PreBurn[]
): ContainerGameResult {
  const { rng, firePositions } = createSeededGameRng(
    seed,
    config.gridSize,
    config.startingFires
  );

  let grid = fireSpreadService.initializeGridFromPositions(
    fireSpreadService.createGrid(config.gridSize),
    firePositions
  );

  let preBurnsApplied = 0;
  let gameStatus: 'win' | 'lose' | null = null;

  for (const [row, col] of preBurns) {
    if (gameStatus) break;
    if (grid[row]?.[col] !== 'alive') continue;

    const burnedGrid = grid.map((r) => [...r]);
    burnedGrid[row][col] = 'burned';
    preBurnsApplied += 1;
    grid = fireSpreadService.updateGrid(burnedGrid, config, rng);
    gameStatus = fireSpreadService.checkGameOver(grid, true);
  }

  return {
    finalGrid: grid,
    preBurnsApplied,
    gameStatus,
    survivalPercentage: fireSpreadService.calculateSurvival(grid),
  };
}
